import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileDown, Plus, Ticket, Loader2, Ban, FileText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api, { apiDownload } from '@/lib/api';

export default function AdminCommands() {
  const { id } = useParams();
  const [stage, setStage] = useState(null);
  const [commands, setCommands] = useState([]);
  const [openGen, setOpenGen] = useState(false);
  const [gen, setGen] = useState({ quantity: 60, start_number: 1, prefix: '' });
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(null);

  const load = useCallback(async () => {
    const [s, c] = await Promise.all([
      api.get(`/admin/stages/${id}`),
      api.get(`/admin/stages/${id}/commands`),
    ]);
    setStage(s.data);
    setCommands(c.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (gen.quantity < 1 || gen.quantity > 5000) {
      toast.error('Quantidade entre 1 e 5000.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/stages/${id}/commands/generate`, gen);
      toast.success(`${gen.quantity} comandas geradas!`);
      setOpenGen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      toast.info('Gerando PDF... pode levar até 1 minuto para muitas comandas.');
      const r = await apiDownload.get(`/admin/stages/${id}/commands/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
    } catch (e) {
      const msg = e.code === 'ECONNABORTED'
        ? 'Tempo esgotado ao gerar PDF. Tente novamente ou gere em lotes menores.'
        : (e.response?.data?.detail || 'Erro ao gerar PDF');
      toast.error(msg);
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await apiDownload.get(`/admin/stages/${id}/commands/csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comandas-${stage?.slug || id}.csv`;
      a.click();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar CSV');
    }
  };

  const cancel = async () => {
    if (!confirmCancel) return;
    try {
      await api.post(`/admin/commands/${confirmCancel.id}/cancel`);
      toast.success('Comanda cancelada');
      setConfirmCancel(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  if (!stage) return <div className="text-muted-foreground">Carregando…</div>;

  const used = commands.filter((c) => c.status === 'used').length;
  const cancelled = commands.filter((c) => c.status === 'cancelled').length;
  const available = commands.filter((c) => c.status === 'available').length;

  return (
    <div className="space-y-4">
      <Link to={`/admin/etapas/${id}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white">
        <ArrowLeft className="w-4 h-4" /> Voltar para etapa
      </Link>
      <div>
        <div className="font-display text-4xl text-white">COMANDAS</div>
        <p className="text-muted-foreground">{stage.title}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total" value={commands.length} testId="cmd-total" />
        <Kpi label="Disponíveis" value={available} testId="cmd-available" />
        <Kpi label="Utilizadas" value={used} testId="cmd-used" />
        <Kpi label="Canceladas" value={cancelled} testId="cmd-cancelled" />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button data-testid="generate-commands-button" onClick={() => setOpenGen(true)} className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]">
          <Plus className="w-4 h-4 mr-1" /> Gerar comandas
        </Button>
        <Button data-testid="download-pdf-button" onClick={downloadPdf} variant="secondary" disabled={commands.length === 0}>
          <FileText className="w-4 h-4 mr-1" /> Baixar PDF (A4, 12/folha)
        </Button>
        <Button data-testid="download-csv-button" onClick={downloadCsv} variant="secondary" disabled={commands.length === 0}>
          <FileDown className="w-4 h-4 mr-1" /> Exportar CSV
        </Button>
      </div>

      {commands.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground"><Ticket className="w-8 h-8 mx-auto mb-2" />Nenhuma comanda gerada ainda.</CardContent></Card>
      ) : (
        <Card className="border border-border bg-card">
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--g66-surface)] sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs uppercase text-muted-foreground">Nº</th>
                    <th className="px-3 py-2 text-left text-xs uppercase text-muted-foreground">Token</th>
                    <th className="px-3 py-2 text-left text-xs uppercase text-muted-foreground">Código</th>
                    <th className="px-3 py-2 text-left text-xs uppercase text-muted-foreground">Status</th>
                    <th className="px-3 py-2 text-right text-xs uppercase text-muted-foreground">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {commands.map((c) => (
                    <tr key={c.id} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-white">{c.visible_number}</td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{c.token}</td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{c.short_code}</td>
                      <td className="px-3 py-2">
                        {c.status === 'available' && <span className="text-[var(--g66-amber)]">Disponível</span>}
                        {c.status === 'used' && <span className="text-white">Usada</span>}
                        {c.status === 'cancelled' && <span className="text-[var(--g66-red-2)]">Cancelada</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {c.status === 'available' && (
                          <Button size="sm" variant="ghost" onClick={() => setConfirmCancel(c)} data-testid={`cancel-cmd-${c.visible_number}`}>
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={openGen} onOpenChange={setOpenGen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Gerar comandas</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Quantidade</Label>
              <Input type="number" min="1" max="5000" value={gen.quantity} onChange={(e) => setGen({ ...gen, quantity: parseInt(e.target.value || '0', 10) })} className="mt-1" data-testid="gen-quantity" />
              <div className="text-xs text-muted-foreground mt-1">Serão geradas em folhas A4 com 12 comandas cada.</div>
            </div>
            <div>
              <Label>Número inicial</Label>
              <Input type="number" min="1" value={gen.start_number} onChange={(e) => setGen({ ...gen, start_number: parseInt(e.target.value || '1', 10) })} className="mt-1" />
            </div>
            <div>
              <Label>Prefixo (opcional)</Label>
              <Input value={gen.prefix} onChange={(e) => setGen({ ...gen, prefix: e.target.value })} className="mt-1" placeholder="Ex: E1-" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpenGen(false)}>Cancelar</Button>
            <Button data-testid="generate-commands-submit" onClick={generate} disabled={busy} className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Gerar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmCancel} onOpenChange={(o) => !o && setConfirmCancel(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar comanda?</AlertDialogTitle>
            <AlertDialogDescription>A comanda {confirmCancel?.visible_number} não poderá mais ser usada.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={cancel} className="bg-[var(--g66-red)] text-white">Cancelar comanda</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Kpi({ label, value, testId }) {
  return (
    <Card className="border border-border bg-card">
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="font-display text-3xl text-white mt-1" data-testid={testId}>{value}</div>
      </CardContent>
    </Card>
  );
}
